/**
 * API config livrables digitaux (`/api/settings/deliverables/*`).
 *
 * Toutes les routes passent par `apiClient` (intercepteur 401 + cookie
 * HTTP-only). Les typages viennent du codegen OpenAPI.
 */

import { apiClient } from './client'
import type {
  DeliverableConfigUpdateRequest,
  DeliverableDocLang,
  DeliverableDocResponse,
  DeliverableRegenerateAllResponse,
  DeliverableSettingsResponse,
  ShopCarouselImageResponse,
  ShopCarouselScope,
} from '@/types/deliverable-settings'

export const deliverableSettingsApi = {
  // ── Lecture aggrégée — config + docs + progress ────────────────────────
  get: async (): Promise<DeliverableSettingsResponse> => {
    const { data } = await apiClient.get<DeliverableSettingsResponse>('/settings/deliverables')
    return data
  },

  // ── PATCH config (formats / DPI / qualité JPG) ─────────────────────────
  update: async (payload: DeliverableConfigUpdateRequest): Promise<DeliverableSettingsResponse> => {
    const { data } = await apiClient.patch<DeliverableSettingsResponse>(
      '/settings/deliverables',
      payload,
    )
    return data
  },

  // ── Upload PDF accompagnant (FR ou EN) ─────────────────────────────────
  uploadDocument: async (lang: DeliverableDocLang, file: File): Promise<DeliverableDocResponse> => {
    const form = new FormData()
    form.append('file', file)
    const { data } = await apiClient.post<DeliverableDocResponse>(
      '/settings/deliverables/document',
      form,
      {
        params: { lang },
        // Override du default `Content-Type: application/json` de
        // l'instance axios — sans ça, axios 1.x sérialise la FormData
        // en JSON et FastAPI retourne 422 (champ `file` manquant).
        // Le boundary est ajouté par le browser à l'envoi.
        headers: { 'Content-Type': 'multipart/form-data' },
      },
    )
    return data
  },

  // ── Suppression PDF accompagnant ───────────────────────────────────────
  deleteDocument: async (lang: DeliverableDocLang): Promise<void> => {
    await apiClient.delete('/settings/deliverables/document', {
      params: { lang },
    })
  },

  // ── Fan-out batch — régénération de toutes les variantes actives ───────
  regenerateAll: async (): Promise<DeliverableRegenerateAllResponse> => {
    const { data } = await apiClient.post<DeliverableRegenerateAllResponse>(
      '/settings/deliverables/regenerate-all',
    )
    return data
  },

  // ── Images carrousel boutique-wide (multi, scope digital/physical/both) ──
  addShopCarouselImage: async (
    file: File,
    scope: ShopCarouselScope,
  ): Promise<ShopCarouselImageResponse> => {
    const form = new FormData()
    form.append('file', file)
    const { data } = await apiClient.post<ShopCarouselImageResponse>(
      '/settings/shop-carousel-images',
      form,
      {
        params: { scope },
        // Override du default `Content-Type: application/json` de
        // l'instance axios — sans ça, axios 1.x sérialise la FormData
        // en JSON et FastAPI retourne 422 (champ `file` manquant).
        headers: { 'Content-Type': 'multipart/form-data' },
      },
    )
    return data
  },

  updateShopCarouselImageScope: async (
    imageId: string,
    scope: ShopCarouselScope,
  ): Promise<ShopCarouselImageResponse> => {
    const { data } = await apiClient.patch<ShopCarouselImageResponse>(
      `/settings/shop-carousel-images/${imageId}`,
      { scope },
    )
    return data
  },

  reorderShopCarouselImages: async (orderedIds: string[]): Promise<ShopCarouselImageResponse[]> => {
    const { data } = await apiClient.post<ShopCarouselImageResponse[]>(
      '/settings/shop-carousel-images/reorder',
      { ordered_ids: orderedIds },
    )
    return data
  },

  deleteShopCarouselImage: async (imageId: string): Promise<void> => {
    await apiClient.delete(`/settings/shop-carousel-images/${imageId}`)
  },
}
